/**
 * I2 — Background hydrator for the client-side product index.
 *
 * Scalability strategy:
 *   - Runs after `requestIdleCallback` so it never blocks first paint.
 *   - Fetches a bounded slice (`MAX_INDEX_SIZE`) from Shopify — never
 *     the full catalog. Larger catalogs go through server-side search
 *     (Shopify Storefront `products(query:...)` on demand).
 *   - Caches the hydrated slice in `sessionStorage` so repeated
 *     navigations don't re-fetch.
 *   - Idempotent: safe to call from multiple entry points.
 */
import { hydrateProductIndex, productIndex, type IndexedProduct } from "./productIndex";
import { storefrontApiRequest } from "./shopify";

const CACHE_KEY = "nardy_product_index_v1";
const CACHE_TTL_MS = 30 * 60 * 1000;
const MAX_INDEX_SIZE = 250; // bounded; server-side search handles the rest

const INDEX_QUERY = `
  query IndexProducts($first: Int!) {
    products(first: $first, sortKey: BEST_SELLING) {
      edges { node { id title handle vendor productType } }
    }
  }
`;

let inflight: Promise<void> | null = null;

function loadFromCache(): IndexedProduct[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; items: IndexedProduct[] };
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.items;
  } catch { return null; }
}

function saveToCache(items: IndexedProduct[]) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items }));
  } catch { /* quota */ }
}

async function doHydrate(): Promise<void> {
  const cached = loadFromCache();
  if (cached) { hydrateProductIndex(cached); return; }
  try {
    const data = await storefrontApiRequest(INDEX_QUERY, { first: MAX_INDEX_SIZE });
    const edges = data?.data?.products?.edges || [];
    const items: IndexedProduct[] = edges.map((e: any) => ({
      slug: e.node.handle,
      partNumber: e.node.title,
      brand: e.node.vendor || undefined,
      category: e.node.productType || undefined,
      title: e.node.title,
    }));
    if (items.length > 0) {
      hydrateProductIndex(items);
      saveToCache(items);
    }
  } catch (err) {
    if (import.meta.env.DEV) console.warn("[productIndex] hydrate failed:", err);
  }
}

/** Kick off hydration on next idle tick. Non-blocking. Safe to call multiple times. */
export function scheduleProductIndexHydration(): void {
  if (typeof window === "undefined") return;
  if (productIndex.isLoaded()) return;
  if (inflight) return;
  const start = () => { inflight = doHydrate().finally(() => { inflight = null; }); };
  const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => number);
  if (ric) ric(start, { timeout: 4000 });
  else setTimeout(start, 1200);
}